import { useSelector } from '@xstate/react';
import { ActorRefFrom } from 'xstate';
import { FormDialog, FormSection } from '~/components';
import { AddressFormValues } from '~/features/types';
import type { profileFormMachine } from '../profileForm.machine';
import { ContactFormValues, EmailFormValues, PersonalFormValues, PhoneFormValues } from '../types';
import { AddressFormWrapper } from './AddressFormWrapper';
import { AddressTable } from './AddressTable';
import { EmailForm } from './EmailForm';
import { EmailTable } from './EmailTable';
import { PhoneForm } from './PhoneForm';
import { PhoneTable } from './PhoneTable';

interface ContactInfoProps {
  values: Partial<ContactFormValues> & Partial<PersonalFormValues>;
  actor: ActorRefFrom<typeof profileFormMachine>;
}

// If the incoming item is marked primary, unset primary on every sibling so
// only one is primary at a time. Backends enforce this; if we don't enforce
// client-side, the server silently keeps the previous primary and the user's
// toggle appears to "not stick".
const enforceSinglePrimary = <T extends { primaryFlag: boolean }>(
  list: T[],
  changedIndex: number
): T[] => {
  if (!list[changedIndex]?.primaryFlag) return list;
  return list.map((item, i) => (i === changedIndex ? item : { ...item, primaryFlag: false }));
};

export const ContactInformationView = ({ actor }: ContactInfoProps) => {
  const addresses = useSelector(actor, (state) => state.context.addresses);
  const phoneNumbers = useSelector(actor, (state) => state.context.phoneNumbers);
  const emailAddresses = useSelector(actor, (state) => state.context.emailAddresses);
  const editPhoneNumberIndex = useSelector(actor, (state) => state.context.editPhoneNumberIndex);
  const editEmailAddressIndex = useSelector(actor, (state) => state.context.editEmailAddressIndex);
  const modal = useSelector(actor, (state) => state.context.modal);
  const { edit, create, type } = modal;

  const handleClose = () => actor.send({ type: 'ON_CANCEL_ADD_OR_EDIT_MODAL' });

  // Chain the item event (context update + modal close) with ON_SUBMIT_CONTACT
  // (PATCH to the server) so every modal submit persists in one click.
  const persistEmails = (updated: EmailFormValues[]) => {
    actor.send({ type: 'ON_SUBMIT_EMAIL_ADDRESSES', values: updated });
    actor.send({
      type: 'ON_SUBMIT_CONTACT',
      values: { addresses, emailAddresses: updated, phoneNumbers },
    });
  };

  const persistPhones = (updated: PhoneFormValues[]) => {
    actor.send({ type: 'ON_SUBMIT_PHONE_NUMBERS', values: updated });
    actor.send({
      type: 'ON_SUBMIT_CONTACT',
      values: { addresses, emailAddresses, phoneNumbers: updated },
    });
  };

  const addEmailAddress = (email: EmailFormValues) => {
    const next = [...emailAddresses, email];
    persistEmails(enforceSinglePrimary(next, next.length - 1));
  };

  const addPhoneNumber = (phoneNumber: PhoneFormValues) => {
    const next = [...phoneNumbers, phoneNumber];
    persistPhones(enforceSinglePrimary(next, next.length - 1));
  };

  const editPhoneNumber = (phoneNumber: PhoneFormValues) => {
    const next = [...phoneNumbers];
    next[editPhoneNumberIndex] = phoneNumber;
    persistPhones(enforceSinglePrimary(next, editPhoneNumberIndex));
  };

  const editEmailAddress = (email: EmailFormValues) => {
    const next = [...emailAddresses];
    next[editEmailAddressIndex] = email;
    persistEmails(enforceSinglePrimary(next, editEmailAddressIndex));
  };

  // Delete handlers bypass ON_DELETE_* (which only updates context) and go
  // straight through ON_SUBMIT_CONTACT so deletions also auto-persist.
  const deleteEmail = (index: number) => {
    const next = emailAddresses.filter((_, i) => i !== index);
    actor.send({ type: 'ON_DELETE_EMAIL', index });
    actor.send({
      type: 'ON_SUBMIT_CONTACT',
      values: { addresses, emailAddresses: next, phoneNumbers },
    });
  };

  const deletePhone = (index: number) => {
    const next = phoneNumbers.filter((_, i) => i !== index);
    actor.send({ type: 'ON_DELETE_PHONE', index });
    actor.send({
      type: 'ON_SUBMIT_CONTACT',
      values: { addresses, emailAddresses, phoneNumbers: next },
    });
  };

  const deleteAddress = (index: number) => {
    const next: AddressFormValues[] = addresses.filter((_, i) => i !== index);
    actor.send({ type: 'ON_DELETE_ADDRESS', index });
    actor.send({
      type: 'ON_SUBMIT_CONTACT',
      values: { addresses: next, emailAddresses, phoneNumbers },
    });
  };

  const dialogTitle = (type: string) => {
    switch (type) {
      case 'email':
        return 'Email address';
      case 'address':
        return 'Address';
      case 'phone':
        return 'Phone Number';
      default:
        return '';
    }
  };
  return (
    <>
      <FormSection componentsPerLine={1}>
        <EmailTable actor={actor} onDelete={deleteEmail} />
        <AddressTable actor={actor} onDelete={deleteAddress} />
        <PhoneTable actor={actor} onDelete={deletePhone} />
      </FormSection>
      <FormDialog title={dialogTitle(type)} onClose={handleClose} editing={edit} creating={create}>
        {type === 'email' && (
          <EmailForm addEmail={addEmailAddress} editEmail={editEmailAddress} actor={actor} />
        )}
        {type === 'address' && <AddressFormWrapper actor={actor} />}
        {type === 'phone' && (
          <PhoneForm addNumber={addPhoneNumber} editNumber={editPhoneNumber} actor={actor} />
        )}
      </FormDialog>
    </>
  );
};
